import { all, call, select } from "redux-saga/effects";

import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import NoteCompletionCardData from "@app/scene/dashboard/store/cards/noteCompletion/NoteCompletionCardData";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import NoteCompletionCardConfiguration from "@app/types/dashboard/cards/noteCompletion/NoteCompletionCardConfiguration";
import Scope from "@app/types/dashboard/Scope";

// Base $filters shared by both counts — check-ins (reliability != null) completed in the range,
// matching the CheckIns card. `note: { $ne: null }` (with-notes count) compiles to an ES `exists`.
export const buildNoteCompletionFilters = (
  configuration: NoteCompletionCardConfiguration,
  assigneeIds: number[] | undefined
) => {
  const { criteria, dateRange } = configuration;
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  return {
    assigneeId: assigneeIds ? { [PlatformFilterOperator.CONTAINS]: assigneeIds } : undefined,
    completedAt: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    crmActivityTypeId: criteria.activityTypeIds.length
      ? { $in: criteria.activityTypeIds }
      : undefined,
    reliability: { [PlatformFilterOperator.NOT_EQUAL]: null },
    source: criteria.sources.length ? { $in: criteria.sources } : undefined,
  };
};

export function* fetchNoteCompletionCardDataHelper(params: {
  callback: (data: NoteCompletionCardData) => void;
  configuration: NoteCompletionCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope.teamId) : undefined;
  const assigneeIds = scope?.userId ? [scope.userId] : scopeTeam?.users.map(({ id }) => id);

  const baseFilters = buildNoteCompletionFilters(configuration, assigneeIds);

  const totalPayload = { $filters: baseFilters, $limit: 0 };
  const withNotesPayload = {
    $filters: { ...baseFilters, note: { [PlatformFilterOperator.NOT_EQUAL]: null } },
    $limit: 0,
  };

  const [totalResponse, withNotesResponse]: [ListResponse<Activity>, ListResponse<Activity>] =
    yield all([
      callApi("fetchActivities", orgId, totalPayload),
      callApi("fetchActivities", orgId, withNotesPayload),
    ]);

  // Store the two raw counts; the percentage is derived in the component.
  yield call(callback, {
    activitiesWithNotesCount: withNotesResponse.total,
    totalActivitiesCount: totalResponse.total,
  });
}
